<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        class="mobile-grid-options-overlay fixed inset-0 z-50 flex p-4"
        :class="overlayClass"
        @click.self="close"
      >
        <div
          class="mobile-grid-options-card bg-white rounded-lg shadow-[0_3px_10px_rgba(20,20,22,0.1)] border border-[#E7E6E1] w-[277px] flex flex-col overflow-hidden"
          :class="cardMaxHeightClass"
          role="dialog"
          aria-label="Grid options"
        >
          <div class="mobile-grid-options-scroll flex-1 overflow-y-auto">
            <!-- Availability banner (Figma 7765:182463 "Availability banner").
                 Shown when the modal was opened from the shape strip's
                 availability link, so the user knows the current grid does
                 not support the shapes they are looking at. -->
            <section
              v-if="highlightedGridIds.length"
              class="mobile-grid-options-availability px-[18px] pt-[14px] pb-[12px]"
            >
              <p class="text-[12px] font-bold leading-[15px] text-[#313558]">
                Not available on this grid
              </p>
              <p class="text-[11px] font-medium leading-[14px] text-[#6B6B72] mt-[4px]">
                This product works on the highlighted grids. Pick one to switch.
              </p>
            </section>

            <!-- GRID SIZE (Figma 4832:137202 HorizontalBorder 1) -->
            <section class="mobile-grid-options-section">
              <div class="flex items-center justify-between px-[18px] py-[14px]">
                <h3 class="text-[12px] font-extrabold text-[#9A9994] uppercase tracking-wide">
                  Grid Size
                </h3>
                <div class="flex flex-col gap-[5px]">
                  <!-- Width -->
                  <div class="flex items-center gap-[5px]">
                    <span class="w-[39px] text-[11px] font-semibold text-[#8C8B86]">Width</span>
                    <input
                      v-model.number="widthFt"
                      type="number"
                      min="0"
                      aria-label="Width in feet"
                      class="mobile-grid-input"
                    >
                    <span class="text-[11px] font-semibold text-[#8C8B86]">ft</span>
                    <input
                      v-model.number="widthIn"
                      type="number"
                      min="0"
                      max="11"
                      aria-label="Width in inches"
                      class="mobile-grid-input"
                    >
                    <span class="text-[11px] font-semibold text-[#8C8B86]">in</span>
                  </div>
                  <!-- Height -->
                  <div class="flex items-center gap-[5px]">
                    <span class="w-[39px] text-[11px] font-semibold text-[#8C8B86]">Height</span>
                    <input
                      v-model.number="heightFt"
                      type="number"
                      min="0"
                      aria-label="Height in feet"
                      class="mobile-grid-input"
                    >
                    <span class="text-[11px] font-semibold text-[#8C8B86]">ft</span>
                    <input
                      v-model.number="heightIn"
                      type="number"
                      min="0"
                      max="11"
                      aria-label="Height in inches"
                      class="mobile-grid-input"
                    >
                    <span class="text-[11px] font-semibold text-[#8C8B86]">in</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- BACKGROUND COLOR (Figma 4832:137202 HorizontalBorder 2) -->
            <section class="mobile-grid-options-section">
              <div class="flex items-center justify-between px-[18px] py-[13px]">
                <h3 class="text-[12px] font-extrabold text-[#9A9994] uppercase tracking-wide">
                  Background Color
                </h3>
                <input
                  v-model="bgColor"
                  type="text"
                  data-coloris
                  aria-label="Background color"
                  class="mobile-grid-color-swatch"
                  :style="{ backgroundColor: bgColor }"
                  @change="handleBgChange"
                >
              </div>
            </section>

            <!-- GRID TYPE (Figma 4832:137202 Frame 1148) -->
            <section class="mobile-grid-options-section">
              <div class="px-[10px] pt-[6px] pb-[4px]">
                <h3 class="text-[12px] font-extrabold text-[#9A9994] uppercase tracking-wide mb-[6px]">
                  Grid Type
                </h3>
                <div class="flex flex-col gap-[6px]">
                  <button
                    v-for="pattern in orderedPatterns"
                    :key="pattern.id"
                    type="button"
                    class="mobile-grid-option flex items-center py-[6px] px-[11px] rounded-md text-left transition-colors"
                    :class="[
                      (selectedPatternId || currentPatternId) === pattern.id ? 'bg-[#F0F0F5]' : 'hover:bg-[#FAFAFA]',
                      highlightedGridIds.includes(pattern.id) ? 'mobile-grid-option--available' : '',
                    ]"
                    :aria-pressed="(selectedPatternId || currentPatternId) === pattern.id"
                    @click="selectPattern(pattern)"
                  >
                    <!-- "Most Popular" pill on the Square grid option (Figma 4832:137202).
                         Positioned absolute against the option button (relative parent). -->
                    <span
                      v-if="pattern.id === GRID_PATTERN_IDS.SQUARE"
                      class="most-popular-pill absolute -top-1 right-0 bg-[#313558] text-white text-[10px] font-semibold px-[8px] py-[4px] rounded-full"
                    >
                      Most Popular
                    </span>
                    <span class="w-[38px] h-[38px] flex items-center justify-center shrink-0">
                      <img
                        :src="PATTERN_ICONS[pattern.id] || pattern.image"
                        :alt="PATTERN_LABELS[pattern.id] || pattern.label"
                        class="w-[36px] h-[36px] object-contain rounded-sm bg-[#F6F5F2] border border-[#E4E3DE]"
                      >
                    </span>
                    <span
                      class="text-[14px] font-bold flex-1"
                      :class="currentPatternId === pattern.id ? 'text-[#313558]' : 'text-[#34343A]'"
                    >
                      {{ PATTERN_LABELS[pattern.id] || pattern.label }}
                    </span>
                    <span
                      v-if="(selectedPatternId || currentPatternId) === pattern.id"
                      class="w-2 h-2 rounded-full bg-[#313558] shrink-0"
                    />
                    <!-- "Available" tag (Figma 7765:182463 "Available tag":
                         59x17 navy pill, 9px bold white text). -->
                    <span
                      v-if="highlightedGridIds.includes(pattern.id)"
                      class="mobile-grid-option__available-tag shrink-0 bg-[#313558] text-white text-[9px] font-bold leading-[11px] px-[8px] rounded-full"
                    >
                      Available
                    </span>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { FOOT_IN_PIXELS, GRID_PATTERN_LIST, GRID_PATTERN_IDS, INCH_IN_PIXELS, SELECTABLE_TOOLS } from '~/composables/grid/constants'
import { GRID_MENU } from '~/composables/shapes/constants'
import { applyGridPatternChange } from '~/components/NewStudio/logic'
import type { GridPattern } from '~/types/grid.types'

const PATTERN_ICONS: Record<string, string> = {
  [GRID_PATTERN_IDS.SQUARE]: '/new-studio/icons/grid-patterns/square.svg',
  [GRID_PATTERN_IDS.SQUARE_45]: '/new-studio/icons/grid-patterns/45-deg.svg',
  [GRID_PATTERN_IDS.HEX]: '/new-studio/icons/grid-patterns/hex.svg',
  [GRID_PATTERN_IDS.HEX_90]: '/new-studio/icons/grid-patterns/hex-90.svg',
  [GRID_PATTERN_IDS.HEX_SPACED]: '/new-studio/icons/grid-patterns/hex-spaced.svg',
  [GRID_PATTERN_IDS.FREEFORM]: '/new-studio/icons/grid-patterns/freeform.svg',
  [GRID_PATTERN_IDS.THREE_D]: '/new-studio/icons/grid-patterns/3d.svg',
}

const PATTERN_LABELS: Record<string, string> = {
  [GRID_PATTERN_IDS.SQUARE]: 'Square',
  [GRID_PATTERN_IDS.SQUARE_45]: '45° Square',
  [GRID_PATTERN_IDS.HEX]: 'Hex',
  [GRID_PATTERN_IDS.HEX_90]: '90° Hex',
  [GRID_PATTERN_IDS.HEX_SPACED]: 'Spaced Hex',
  [GRID_PATTERN_IDS.FREEFORM]: 'Freeform',
  [GRID_PATTERN_IDS.THREE_D]: '3D',
}

const PATTERN_ORDER = [
  GRID_PATTERN_IDS.SQUARE,
  GRID_PATTERN_IDS.SQUARE_45,
  GRID_PATTERN_IDS.HEX,
  GRID_PATTERN_IDS.HEX_90,
  GRID_PATTERN_IDS.HEX_SPACED,
  GRID_PATTERN_IDS.FREEFORM,
  GRID_PATTERN_IDS.THREE_D,
]

const props = withDefaults(defineProps<{
  show: boolean
  /** When provided, selection emits via `select` and the modal does NOT
   *  apply the pattern to the grid. Used by the New/Scratch flow so the
   *  parent can gather canvas dimensions before starting. */
  selectedPatternId?: string | null
  /**
   * 'scratch' — the New/Scratch flow: the parent owns the selection, so the
   *   card is the compact 356px spec size and sits centred over the modal.
   * 'change'  — the Change Grid button: the pattern applies immediately and
   *   the card expands to show every grid type without scrolling.
   *
   * This is an explicit mode rather than being inferred from
   * `selectedPatternId`, because the scratch flow passes `null` until the
   * user picks something.
   */
  mode?: 'scratch' | 'change'
  /** Hide the top-right close button when the parent renders its own close. */
  hideClose?: boolean
  /**
   * Grid ids to mark as available. The shape strip links here when the
   * current tab's shapes are not usable on the active grid, so the user can
   * see which grids do support them - the mobile equivalent of the
   * desktop's GridOptionsPopover "Available" badge.
   */
  highlightedGridIds?: string[]
}>(), { show: false, selectedPatternId: null, mode: 'change', hideClose: false, highlightedGridIds: () => [] })
const emit = defineEmits<{ close: []; select: [pattern: GridPattern] }>()

const gridStore = useGridStore()
const projectStore = useProjectStore()
const runtimeConfig = useRuntimeConfig()

const orderedPatterns = computed(() =>
  PATTERN_ORDER.map(id => GRID_PATTERN_LIST.find(p => p.id === id)!).filter(Boolean)
)
const currentPatternId = computed(() => gridStore.gridPattern?.id)

// "New/Scratch" is the mode where the parent picks the pattern and then
// gathers the canvas dimensions; "Change Grid" applies the pattern directly.
const isScratchFlow = computed(() => props.mode === 'scratch')

const highlightedGridIds = computed(() => props.highlightedGridIds)

// "Change Grid" sits in the top-right under the header (just like the Figma
// change-grid button). "New/Scratch" centers the modal like the original.
const overlayClass = computed(() =>
  isScratchFlow.value
    ? 'items-center justify-center xl:hidden'
    : 'items-start justify-end p-[5px] pt-[80px] xl:hidden'
)

// The card expands to its full content height so all seven grid types are
// visible without scrolling. Figma 4832:137202 shows the card at 356px with
// a scroll bar, but that is the compact viewport - its own content is 551px
// (130px of Grid Size / Background Color, plus the 424px Grid Type list).
// The New/Scratch flow keeps the compact card because it sits over a modal
// with its own scrolling. The remaining max-height is the viewport bound, so
// a short screen still scrolls rather than overflowing.
const cardMaxHeightClass = computed(() =>
  isScratchFlow.value
    ? 'max-h-[356px]'
    : 'max-h-[calc(100dvh-90px)]'
)

// --- Grid Size (mirrors desktop GridOptionsPopover's reactive ft/in fields) ---
const widthFt = ref(gridStore.gridSize.width.ft)
const widthIn = ref(Math.round(gridStore.gridSize.width.in))
const heightFt = ref(gridStore.gridSize.height.ft)
const heightIn = ref(Math.round(gridStore.gridSize.height.in))

let sizeDebounce: ReturnType<typeof setTimeout> | null = null

watch([widthFt, widthIn, heightFt, heightIn], () => {
  if (sizeDebounce) clearTimeout(sizeDebounce)
  sizeDebounce = setTimeout(() => {
    const w = (widthFt.value || 0) * FOOT_IN_PIXELS + (widthIn.value || 0) * INCH_IN_PIXELS
    const h = (heightFt.value || 0) * FOOT_IN_PIXELS + (heightIn.value || 0) * INCH_IN_PIXELS
    if (w > 0 && h > 0) {
      gridStore.setGridSize(w, h)
      if (runtimeConfig.public.autoSaveOnChange) projectStore.save(true, true)
    }
  }, 400)
})

watch(() => gridStore.gridSize, (size) => {
  widthFt.value = size.width.ft
  widthIn.value = Math.round(size.width.in)
  heightFt.value = size.height.ft
  heightIn.value = Math.round(size.height.in)
})

// --- Background Color ---
const bgColor = ref(projectStore.bgColor || gridStore.gridBgColor || 'white')

const handleBgChange = () => {
  gridStore.setBgColor(bgColor.value)
  projectStore.bgColor = bgColor.value
  if (runtimeConfig.public.autoSaveOnChange) projectStore.save()
}

watch(() => gridStore.gridBgColor, (color) => {
  bgColor.value = color
})

const selectPattern = (pattern: GridPattern) => {
  if (isScratchFlow.value) {
    // "New/Scratch" mode: parent decides what to do
    emit('select', pattern)
    return
  }
  // "Change Grid" mode: apply directly
  applyGridPatternChange(gridStore, pattern, { preserveDimensions: true })
  close()
}

const close = () => emit('close')

// Wire up Coloris (singleton) for the background color swatch when shown.
// Coloris is a singleton — safe to re-init; it just applies the config.
onMounted(async () => {
  await import('@melloware/coloris/dist/coloris.css').catch(() => undefined)
  const Coloris = (await import('@melloware/coloris')).default
  Coloris?.init?.()
  Coloris?.({
    themeMode: 'light',
    alpha: false,
    format: 'hex',
  } as any)
  await nextTick(() => Coloris?.init?.())

  const handlePick = () => {
    gridStore.setBgColor(bgColor.value)
    projectStore.bgColor = bgColor.value
    if (runtimeConfig.public.autoSaveOnChange) projectStore.save()
  }
  document.addEventListener('coloris:pick', handlePick)
  onUnmounted(() => document.removeEventListener('coloris:pick', handlePick))
})
</script>

<style scoped>
.mobile-grid-options-card {
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
}

/* Sections separated by a 1px row that's visually subtle in Figma
   (0px stroke #f0efeb). We use a top border to keep the same separator
   without needing a literal divider element. */
.mobile-grid-options-section {
  border-top: 1px solid #f0efeb;
}
.mobile-grid-options-section:first-child {
  border-top: 0;
}

/* Figma Input (30×21, padding 3, 1px stroke #e4e3de, 2px corner-radius).
   We use width:30 height:21 to match, font Montserrat 11px regular. */
.mobile-grid-input {
  width: 30px;
  height: 21px;
  padding: 3px;
  border: 1px solid #e4e3de;
  border-radius: 2px;
  background: #fff;
  font-family: inherit;
  font-size: 11px;
  font-weight: 400;
  text-align: center;
  color: #000;
  -moz-appearance: textfield;
}
.mobile-grid-input::-webkit-outer-spin-button,
.mobile-grid-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.mobile-grid-input:focus {
  outline: 2px solid #28427b;
  outline-offset: 1px;
}

/* Figma Background+Border rectangle (36×24, white fill, 1px stroke #e4e3de,
   2px corner-radius). The text input inside is invisible so we get a
   pure color swatch with Coloris attached. */
.mobile-grid-color-swatch {
  width: 36px;
  height: 24px;
  padding: 0;
  border: 1px solid #e4e3de;
  border-radius: 2px;
  font-size: 0;
  color: transparent;
  cursor: pointer;
}

/* Figma option button: 250×51 (fixed), padding 11px L/R + 9px T/B,
   itemSpacing 14px between thumbnail and label, full row hover bg. */
.mobile-grid-option {
  background: transparent;
  border: 0;
  cursor: pointer;
  width: 100%;
  position: relative;
}

/* Figma 7765:182463 "Highlight" - a 1.5px navy ring with an 8px radius
   around the grids the current shapes are available on. */
.mobile-grid-option--available {
  box-shadow: inset 0 0 0 1.5px #313558;
}

.mobile-grid-option .absolute {
  position: absolute;
  z-index: 1;
}

/* Figma Input element inside the color swatch (the colored fill itself).
   The data-coloris attribute wires Coloris to render the picker when the
   swatch is tapped. The actual <input> is hidden visually behind the
   swatch background. */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>